import { useEffect, useState, type JSX } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@rozman/shared';
import { accountApi, setAccessToken, type CustomerOrder } from '@/lib/api';
import { useLocale, useLocalePath } from '@/hooks/useLocalePath';
import { Price } from '@/components/Price';
import { Seo } from '@/components/Seo';

const TOKEN_KEY = 'rozman.token';

/**
 * Customer account: real order history, plus the RODO controls the spec requires visible —
 * data export and erasure, with the tax-retention exemption stated rather than silently
 * applied.
 */
export function AccountPage(): JSX.Element {
  const { t } = useTranslation();
  const locale = useLocale();
  const path = useLocalePath();
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    setAccessToken(token);
  }, [token]);

  const { data: me } = useQuery({
    queryKey: ['me', token],
    queryFn: accountApi.me,
    enabled: Boolean(token),
    retry: false,
  });

  const { data: orders, isLoading } = useQuery({
    queryKey: ['my-orders', token],
    queryFn: () => accountApi.orders(1),
    enabled: Boolean(token && me),
    retry: false,
  });

  if (!token || !me) {
    return (
      <>
        <Seo title={t('nav.login')} path="/konto" noindex />
        <LoginForm
          onSuccess={(accessToken) => {
            localStorage.setItem(TOKEN_KEY, accessToken);
            setToken(accessToken);
          }}
        />
      </>
    );
  }

  return (
    <>
      <Seo title={t('account.title')} path="/konto" noindex />
      <div className="rz-shell max-w-3xl py-12 space-y-10 md:py-16">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h1 className="text-xl md:text-2xl">{t('account.title')}</h1>
          <button
            type="button"
            className="min-h-10 text-xs underline underline-offset-4 text-[var(--color-muted)]"
            onClick={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
          >
            {t('nav.logout')}
          </button>
        </div>
        <p className="text-sm text-[var(--color-ink-soft)]">
          {me.firstName} {me.lastName} · {me.email}
        </p>

        <section className="space-y-4">
          <h2 className="rz-eyebrow">{t('account.orders')}</h2>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }, (_unused, i) => <div key={i} className="rz-skeleton h-20" />)}
            </div>
          ) : (orders?.items.length ?? 0) === 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-[var(--color-muted)]">{t('account.noOrders')}</p>
              <Link to={path('/')} className="rz-button rz-button--ghost">{t('cart.emptyCta')}</Link>
            </div>
          ) : (
            <ul className="space-y-3">
              {(orders?.items ?? []).map((order) => (
                <OrderRow key={order.id} order={order} locale={locale} />
              ))}
            </ul>
          )}
        </section>

        <PersonalData />
      </div>
    </>
  );
}

function OrderRow({ order, locale }: { order: CustomerOrder; locale: 'pl' | 'uk' }): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <li className="border border-[var(--color-line)] p-4">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="min-w-0">
          <span className="block font-semibold text-sm">{order.number}</span>
          <span className="block text-2xs text-[var(--color-muted)]">
            {formatDate(order.placedAt, locale)} · {t(`orderStatus.${order.status}`)}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-3">
          <Price minor={order.totalGross} className="text-sm font-semibold" />
          <span aria-hidden="true" className="text-[var(--color-muted)]">{open ? '−' : '+'}</span>
        </span>
      </button>

      {open && (
        <div className="mt-4 space-y-3 border-t border-[var(--color-line)] pt-3">
          <ul className="space-y-1.5">
            {order.items.map((item) => (
              <li key={item.id} className="flex justify-between gap-3 text-xs">
                <span className="min-w-0 flex-1">
                  {item.nameSnapshot} <span className="text-[var(--color-muted)]">× {item.quantity}</span>
                </span>
                <Price minor={item.lineTotalGross} className="shrink-0" />
              </li>
            ))}
          </ul>
          {order.shipments.length > 0 && (
            <div className="space-y-1">
              <p className="rz-eyebrow">{t('cart.shipping')}</p>
              {order.shipments.map((s, i) => (
                <p key={i} className="text-2xs text-[var(--color-muted)]">
                  {t('cart.shipmentFrom', { supplier: s.supplierName })} ·{' '}
                  {t('product.deliveryEstimate', { min: s.deliveryMinDays, max: s.deliveryMaxDays })}
                  {s.trackingNumber ? ` · ${s.trackingNumber}` : ''}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/** RODO: export and erasure, with the retention exemption said out loud. */
function PersonalData(): JSX.Element {
  const { t } = useTranslation();
  const [result, setResult] = useState<string | null>(null);

  const exportData = useMutation({
    mutationFn: accountApi.exportData,
    onSuccess: (data) => {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'rozman-moje-dane.json';
      link.click();
      URL.revokeObjectURL(url);
    },
  });

  const erase = useMutation({
    mutationFn: accountApi.erase,
    onSuccess: (r) => setResult(r.retained.join('\n')),
  });

  return (
    <section className="space-y-4 border-t border-[var(--color-line)] pt-8">
      <h2 className="rz-eyebrow">{t('account.personalData')}</h2>
      <div className="space-y-5">
        <div>
          <button type="button" className="rz-button rz-button--ghost" disabled={exportData.isPending} onClick={() => exportData.mutate()}>
            {exportData.isPending ? t('common.loading') : t('account.exportData')}
          </button>
          <p className="mt-1.5 max-w-md text-2xs leading-relaxed text-[var(--color-muted)]">
            {t('account.exportDataHelp')}
          </p>
        </div>
        <div>
          <button
            type="button"
            className="rz-button rz-button--ghost !text-[var(--color-danger)]"
            disabled={erase.isPending}
            onClick={() => {
              if (window.confirm(t('account.deleteAccountConfirm'))) erase.mutate();
            }}
          >
            {t('account.deleteAccount')}
          </button>
          <p className="mt-1.5 max-w-md text-2xs leading-relaxed text-[var(--color-muted)]">
            {t('account.deleteAccountHelp')}
          </p>
          {result && (
            <p className="mt-2 whitespace-pre-line border-l-2 border-[var(--color-accent)] pl-3 text-2xs" role="status">
              {result}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

function LoginForm({ onSuccess }: { onSuccess: (token: string) => void }): JSX.Element {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const login = useMutation({
    mutationFn: () => accountApi.login(email, password),
    onSuccess: (r) => onSuccess(r.accessToken),
    onError: () => setError('validation.invalidCredentials'),
  });

  return (
    <div className="rz-shell max-w-sm py-16 md:py-24">
      <h1 className="text-xl md:text-2xl">{t('nav.login')}</h1>
      <form
        className="mt-6 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          login.mutate();
        }}
      >
        <label className="block space-y-1.5">
          <span className="text-xs font-semibold">{t('checkout.email')}</span>
          <input type="email" className="rz-field" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
        </label>
        <label className="block space-y-1.5">
          <span className="text-xs font-semibold">{t('nav.login')}</span>
          <input type="password" className="rz-field" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
        </label>
        {error && <p role="alert" className="text-2xs text-[var(--color-danger)]">{t(error)}</p>}
        <button type="submit" className="rz-button w-full" disabled={login.isPending}>
          {login.isPending ? t('common.loading') : t('nav.login')}
        </button>
      </form>
      <p className="mt-4 text-2xs text-[var(--color-muted)]">
        Demo: klient@rozman.local / klient12345
      </p>
    </div>
  );
}
